import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  BookOpen,
  Layers3,
  Save,
  WandSparkles,
  Search,
  CheckCircle2,
  Users,
  FileText,
  Award,
  Eye,
  Edit2,
  XCircle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  RotateCw,
  Zap,
  Loader2
} from 'lucide-react';
import api from '../api';
import COEHeader from './COEHeader';
import AcademicContextSelector from './AcademicContextSelector';

const getTenantId = (tenant) => localStorage.getItem('tenantId') || tenant?.id || tenant?._id || tenant?.tenantId || '';

const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;
const formatNumber = (value) => Number(value || 0).toFixed(1);

const StatusBadge = ({ status }) => {
  const configs = {
    complete: { icon: CheckCircle, label: 'Complete', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    partial: { icon: Clock, label: 'Partial', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    absent: { icon: XCircle, label: 'Absent', className: 'bg-rose-50 text-rose-700 border-rose-200' },
    draft: { icon: Edit2, label: 'Draft', className: 'bg-slate-50 text-slate-700 border-slate-200' },
    pending: { icon: Clock, label: 'Pending', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    missing: { icon: AlertCircle, label: 'Missing', className: 'bg-rose-50 text-rose-700 border-rose-200' }
  };
  const config = configs[status?.toLowerCase()] || configs.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.className}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// ============================================================
// CONFIRMATION MODAL COMPONENT
// ============================================================
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, isLoading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">{message}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6 justify-end">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            {cancelText || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isLoading ? 'Processing...' : (confirmText || 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// RESULT MODAL COMPONENT
// ============================================================
const ResultModal = ({ isOpen, onClose, type, title, message, details }) => {
  if (!isOpen) return null;

  const isSuccess = type === 'success';
  const Icon = isSuccess ? CheckCircle2 : AlertCircle;
  const bgColor = isSuccess ? 'bg-emerald-100' : 'bg-rose-100';
  const iconColor = isSuccess ? 'text-emerald-600' : 'text-rose-600';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full ${bgColor} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500 mt-1">{message}</p>
            {details && (
              <div className="mt-3 p-3 bg-slate-50 rounded-xl text-xs text-slate-600 font-mono">
                {details}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const EndExamMarksManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const user = state.user || null;
  const tenant = state.tenant || null;
  const orgInfo = state.orgInfo || { name: '', logoUrl: '' };

  const [context, setContext] = useState(state.context || null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjectData, setSubjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkEnhancing, setBulkEnhancing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [rawMarks, setRawMarks] = useState({});
  const [aggregationEdits, setAggregationEdits] = useState({});
  const [questionMarks, setQuestionMarks] = useState({});
  const [bulkPercent, setBulkPercent] = useState(10);
  const [bulkOnlyBelowMax, setBulkOnlyBelowMax] = useState(true);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [viewMode, setViewMode] = useState('summary');
  const [recalcProgress, setRecalcProgress] = useState(null);

  // Modal states
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    isLoading: false
  });

  const [resultModal, setResultModal] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
    details: ''
  });

  const goBack = () => navigate('/coe', { replace: true, state: { user, tenant, orgInfo } });

  const showStatus = useCallback((type, message) => {
    setStatusMessage({ type, message });
    window.clearTimeout(showStatus._timer);
    showStatus._timer = window.setTimeout(() => setStatusMessage({ type: '', message: '' }), 5000);
  }, []);

  const showResult = useCallback((type, title, message, details = '') => {
    setResultModal({
      isOpen: true,
      type,
      title,
      message,
      details
    });
  }, []);

  const closeResult = useCallback(() => {
    setResultModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  const closeConfirmation = useCallback(() => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  }, []);

  const isContextReady = useMemo(() => {
    if (!context?.programTypeId || !context?.programId || !context?.academicYearId) return false;
    return Boolean(context?.semesterId || context?.semId || context?.termId);
  }, [context]);

  const fetchOverview = useCallback(async () => {
    if (!isContextReady) {
      setSubjects([]);
      setSelectedSubject(null);
      setSubjectData(null);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('programTypeId', context.programTypeId);
      params.append('programId', context.programId);
      params.append('academicYearId', context.academicYearId);
      if (context.semesterId || context.semId) params.append('semesterId', context.semesterId || context.semId);
      if (context.termId) params.append('termId', context.termId);

      const response = await api.get(`/api/coe/end-exam-marks/overview?${params.toString()}`, {
        headers: { 'x-tenant-id': getTenantId(tenant) }
      });

      if (response.data.success) {
        setSubjects(response.data.data.subjects || []);
      }
    } catch (err) {
      console.error('Failed to fetch overview:', err);
      setError(err.response?.data?.message || 'Failed to fetch end exam overview');
    } finally {
      setLoading(false);
    }
  }, [context, isContextReady, tenant]);

  const fetchSubjectDetail = useCallback(async (subject) => {
    if (!subject || !isContextReady) return;

    setDetailLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('programTypeId', context.programTypeId);
      params.append('programId', context.programId);
      params.append('academicYearId', context.academicYearId);
      if (context.semesterId || context.semId) params.append('semesterId', context.semesterId || context.semId);
      if (context.termId) params.append('termId', context.termId);

      const response = await api.get(`/api/coe/end-exam-marks/subject/${subject.subjectId}?${params.toString()}`, {
        headers: { 'x-tenant-id': getTenantId(tenant) }
      });

      if (response.data.success) {
        setSubjectData(response.data.data);
        const aggregateDrafts = {};
        setSelectedSubject(subject);
        const marksMap = {};
        (response.data.data.students || []).forEach((student) => {
          if (student.aggregation?.id) {
            aggregateDrafts[student.aggregation.id] = student.aggregation.totalEndExamScore ?? 0;
          }
          (student.rawEntries || []).forEach((entry) => {
            marksMap[entry._id] = Number(entry.totalMarksObtained || 0);
          });
        });
        setRawMarks(marksMap);
        setAggregationEdits(aggregateDrafts);
        const questionDrafts = {};
        (response.data.data.students || []).forEach((student) => {
          (student.rawEntries || []).forEach((entry) => {
            (entry.questionWiseMarks || []).forEach((question) => {
              const questionKey = `${entry._id}:${question.questionId || question.questionNumber || question.sectionName || question._id || 'question'}`;
              questionDrafts[questionKey] = question.obtainedMarks ?? question.marksObtained ?? 0;
            });
          });
        });
        setQuestionMarks(questionDrafts);
      }
    } catch (err) {
      console.error('Failed to fetch subject detail:', err);
      setError(err.response?.data?.message || 'Failed to fetch subject end exam marks');
    } finally {
      setDetailLoading(false);
    }
  }, [context, isContextReady, tenant]);

  useEffect(() => {
    if (isContextReady) {
      fetchOverview();
    }
  }, [fetchOverview, isContextReady]);

  const filteredSubjects = useMemo(() => {
    return subjects.filter((subject) => {
      const text = `${subject.subjectName || ''} ${subject.subjectCode || ''}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [search, subjects]);

  const selectedSubjectStats = useMemo(() => {
    if (!selectedSubject) return null;
    return subjects.find((subject) => String(subject.subjectId) === String(selectedSubject.subjectId)) || selectedSubject;
  }, [selectedSubject, subjects]);

  const subjectStudents = useMemo(() => {
    return subjectData?.students || [];
  }, [subjectData]);

  const summaryStats = useMemo(() => {
    if (!subjectStudents.length) return null;
    
    const totalStudents = subjectStudents.length;
    const withMarks = subjectStudents.filter(s => s.rawEntries?.length > 0).length;
    const withAggregation = subjectStudents.filter(s => s.aggregation).length;
    const avgPercentage = subjectStudents.reduce((sum, s) => sum + (s.aggregation?.endExamPercentage || 0), 0) / totalStudents;
    const passed = subjectStudents.filter(s => (s.aggregation?.endExamPercentage || 0) >= 40).length;
    const failed = totalStudents - passed;
    
    return { totalStudents, withMarks, withAggregation, avgPercentage, passed, failed };
  }, [subjectStudents]);

  const openSubject = useCallback(async (subject) => {
    await fetchSubjectDetail(subject);
  }, [fetchSubjectDetail]);

  // ============================================================
  // RECALCULATE SUBJECT - FIXED
  // ============================================================
  const handleRecalculateSubject = useCallback(() => {
    if (!selectedSubject || !isContextReady) {
      showResult('error', 'Cannot Recalculate', 'Please select a subject and valid academic context first.');
      return;
    }

    // Open confirmation modal
    setConfirmationModal({
      isOpen: true,
      title: 'Recalculate Subject Aggregations',
      message: `This will recalculate end exam aggregations for all students in "${selectedSubjectStats?.subjectName || selectedSubject?.subjectName || 'this subject'}". Continue?`,
      confirmText: 'Recalculate',
      cancelText: 'Cancel',
      isLoading: false,
      onConfirm: async () => {
        // Set loading state
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));
        setRecalculating(true);
        setRecalcProgress(null);
        
        try {
          console.log('📊 [Frontend] Recalculating subject:', selectedSubject.subjectId);
          
          const response = await api.post('/api/coe/end-exam-marks/recalculate-subject', {
            subjectId: selectedSubject.subjectId,
            programTypeId: context.programTypeId,
            programId: context.programId,
            academicYearId: context.academicYearId,
            semesterId: context.semesterId || context.semId,
            termId: context.termId,
            forceRecalculate: true
          }, {
            headers: { 'x-tenant-id': getTenantId(tenant) }
          });

          console.log('📊 [Frontend] Response received:', response.data);

          // IMPORTANT: Close confirmation modal FIRST
          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setRecalculating(false);

          if (response.data.success) {
            const data = response.data.data;
            const successCount = data.results?.success || data.totalStudents || 0;
            const failedCount = data.results?.failed || 0;
            
            setRecalcProgress({
              total: data.totalStudents || 0,
              success: successCount,
              failed: failedCount
            });

            showResult(
              'success',
              'Recalculation Complete!',
              `Successfully processed ${successCount} students${failedCount > 0 ? `, ${failedCount} failed` : ''}`,
              failedCount > 0 ? `Failed: ${failedCount} students` : ''
            );

            // Refresh data
            await fetchOverview();
            if (selectedSubject) {
              await fetchSubjectDetail(selectedSubject);
            }
          } else {
            showResult('error', 'Recalculation Failed', response.data.message || 'Unknown error occurred');
          }
        } catch (err) {
          console.error('❌ [Frontend] Recalculation failed:', err);
          
          // Close confirmation modal on error
          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setRecalculating(false);

          let errorMessage = 'Failed to recalculate aggregations';
          let errorDetails = '';
          
          if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            errorMessage = 'Request timed out. The operation may still be processing in the background.';
            errorDetails = 'Check the subject status after a few moments.';
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
            errorDetails = err.response.data.error || '';
          } else if (err.message) {
            errorMessage = err.message;
          }

          showResult('error', 'Recalculation Failed', errorMessage, errorDetails);
        }
      }
    });
  }, [selectedSubject, isContextReady, context, tenant, selectedSubjectStats, fetchOverview, fetchSubjectDetail, showResult]);

  // ============================================================
  // RECALCULATE ALL SUBJECTS
  // ============================================================
  const handleRecalculateAllSubjects = useCallback(() => {
    if (!isContextReady) {
      showResult('error', 'Cannot Recalculate', 'Please select a valid academic context first.');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Recalculate All Subject Aggregations',
      message: `This will recalculate end exam aggregations for ALL subjects in the current context. This may take a few moments. Continue?`,
      confirmText: 'Recalculate All',
      cancelText: 'Cancel',
      isLoading: false,
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));
        setRecalculatingAll(true);
        setRecalcProgress(null);
        
        try {
          console.log('📊 [Frontend] Recalculating ALL subjects...');
          
          const response = await api.post('/api/coe/end-exam-marks/recalculate-context', {
            programTypeId: context.programTypeId,
            programId: context.programId,
            academicYearId: context.academicYearId,
            semesterId: context.semesterId || context.semId,
            termId: context.termId
          }, {
            headers: { 'x-tenant-id': getTenantId(tenant) }
          });

          console.log('📊 [Frontend] Response received:', response.data);

          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setRecalculatingAll(false);

          if (response.data.success) {
            const data = response.data.data;
            const processed = data.processed || data.totalSubjects || 0;
            const failed = data.failed || 0;
            
            setRecalcProgress({
              total: data.totalSubjects || 0,
              success: processed,
              failed: failed
            });

            showResult(
              'success',
              'Bulk Recalculation Complete!',
              `Successfully processed ${processed} subjects${failed > 0 ? `, ${failed} failed` : ''}`,
              failed > 0 ? `Failed: ${failed} subjects` : ''
            );

            await fetchOverview();
            if (selectedSubject) {
              await fetchSubjectDetail(selectedSubject);
            }
          } else {
            showResult('error', 'Bulk Recalculation Failed', response.data.message || 'Unknown error occurred');
          }
        } catch (err) {
          console.error('❌ [Frontend] Bulk recalculation failed:', err);
          
          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setRecalculatingAll(false);

          let errorMessage = 'Failed to recalculate all aggregations';
          let errorDetails = '';
          
          if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            errorMessage = 'Request timed out. The operation may still be processing in the background.';
            errorDetails = 'Check the subject status after a few moments.';
          } else if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
            errorDetails = err.response.data.error || '';
          } else if (err.message) {
            errorMessage = err.message;
          }

          showResult('error', 'Bulk Recalculation Failed', errorMessage, errorDetails);
        }
      }
    });
  }, [isContextReady, context, tenant, selectedSubject, fetchOverview, fetchSubjectDetail, showResult]);

  // ============================================================
  // BULK ENHANCE
  // ============================================================
  const handleBulkEnhance = useCallback(() => {
    if (!isContextReady) {
      showResult('error', 'Cannot Enhance', 'Please select a valid academic context first.');
      return;
    }

    setConfirmationModal({
      isOpen: true,
      title: 'Bulk Enhancement',
      message: `This will increase raw marks by ${bulkPercent}% for all students${bulkOnlyBelowMax ? ' (only below max)' : ''}. Continue?`,
      confirmText: `Enhance ${bulkPercent}%`,
      cancelText: 'Cancel',
      isLoading: false,
      onConfirm: async () => {
        setConfirmationModal(prev => ({ ...prev, isLoading: true }));
        setBulkEnhancing(true);
        
        try {
          const response = await api.post('/api/coe/end-exam-marks/bulk-enhance', {
            programTypeId: context.programTypeId,
            programId: context.programId,
            academicYearId: context.academicYearId,
            semesterId: context.semesterId || context.semId,
            termId: context.termId,
            subjectId: selectedSubject?.subjectId,
            enhancementPercent: Number(bulkPercent),
            onlyBelowMax: bulkOnlyBelowMax
          }, {
            headers: { 'x-tenant-id': getTenantId(tenant) }
          });

          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setBulkEnhancing(false);

          if (response.data.success) {
            const data = response.data.data;
            showResult(
              'success',
              'Bulk Enhancement Complete!',
              `Updated ${data.updatedCount || 0} marks, skipped ${data.skippedCount || 0}`,
              `Recalculated ${data.recalcCount || 0} aggregations`
            );
            await fetchOverview();
            if (selectedSubject) {
              await fetchSubjectDetail(selectedSubject);
            }
          } else {
            showResult('error', 'Bulk Enhancement Failed', response.data.message || 'Unknown error occurred');
          }
        } catch (err) {
          console.error('Bulk enhance failed:', err);
          setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
          setBulkEnhancing(false);
          showResult('error', 'Bulk Enhancement Failed', err.response?.data?.message || 'Failed to bulk enhance marks');
        }
      }
    });
  }, [isContextReady, context, tenant, selectedSubject, bulkPercent, bulkOnlyBelowMax, fetchOverview, fetchSubjectDetail, showResult]);

  // ============================================================
  // SAVE RAW MARK
  // ============================================================
  const handleSaveRawMark = useCallback(async (mark) => {
    setSaving(true);
    try {
      const questionWiseMarksPayload = Array.isArray(mark.questionWiseMarks) && mark.questionWiseMarks.length > 0
        ? mark.questionWiseMarks.map((question) => {
            const questionKey = `${mark._id}:${question.questionId || question.questionNumber || question.sectionName || question._id || 'question'}`;
            const obtainedMarks = Number(questionMarks[questionKey] ?? question.obtainedMarks ?? question.marksObtained ?? 0);
            return {
              ...question,
              obtainedMarks,
              marksObtained: obtainedMarks,
              maxMarks: Number(question.maxMarks || 0)
            };
          })
        : [];

      const derivedTotalFromQuestions = questionWiseMarksPayload.length > 0
        ? questionWiseMarksPayload.reduce((sum, question) => sum + Number(question.obtainedMarks || 0), 0)
        : Number(rawMarks[mark._id] ?? mark.rawEntries?.[0]?.totalMarksObtained ?? 0);

      const payload = {
        totalMarksObtained: derivedTotalFromQuestions,
        questionWiseMarks: questionWiseMarksPayload,
        remarks: mark.remarks || '',
        isAbsent: !!mark.isAbsent,
        status: mark.status || 'submitted'
      };

      const response = await api.put(`/api/coe/end-exam-marks/mark/${mark._id}`, payload, {
        headers: { 'x-tenant-id': getTenantId(tenant) }
      });

      if (response.data.success) {
        showStatus('success', 'Raw marks updated and aggregation recalculated');
        await fetchOverview();
        await fetchSubjectDetail(selectedSubjectStats || selectedSubject);
      }
    } catch (err) {
      console.error('Failed to update mark:', err);
      showStatus('error', err.response?.data?.message || 'Failed to update raw mark');
    } finally {
      setSaving(false);
    }
  }, [questionMarks, rawMarks, tenant, selectedSubjectStats, selectedSubject, fetchOverview, fetchSubjectDetail, showStatus]);

  // ============================================================
  // SAVE AGGREGATION
  // ============================================================
  const handleSaveAggregation = useCallback(async (student) => {
    if (!student?.aggregation?.id) return;

    setSaving(true);
    try {
      const totalEndExamScore = Number(aggregationEdits[student.aggregation.id] ?? student.aggregation.totalEndExamScore ?? 0);
      const response = await api.put(`/api/coe/end-exam-marks/aggregation/${student.aggregation.id}`, {
        totalEndExamScore
      }, {
        headers: { 'x-tenant-id': getTenantId(tenant) }
      });

      if (response.data.success) {
        showStatus('success', 'Aggregated end exam total updated');
        await fetchOverview();
        await fetchSubjectDetail(selectedSubjectStats || selectedSubject);
      }
    } catch (err) {
      console.error('Failed to update aggregation:', err);
      showStatus('error', err.response?.data?.message || 'Failed to update aggregated marks');
    } finally {
      setSaving(false);
    }
  }, [aggregationEdits, tenant, selectedSubjectStats, selectedSubject, fetchOverview, fetchSubjectDetail, showStatus]);

  const toggleStudentExpand = (studentId) => {
    setExpandedStudent(expandedStudent === studentId ? null : studentId);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={goBack} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <button onClick={goBack} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900">
              <ArrowLeft className="w-4 h-4" /> Back to COE
            </button>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 mt-2">End Exam Marks Manager</h1>
            <p className="text-sm text-slate-500 mt-1">Review, edit, and manage end exam marks with real-time aggregation</p>
          </div>
          <button
            onClick={fetchOverview}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Context Selector */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4">
          <AcademicContextSelector
            tenantId={getTenantId(tenant)}
            onContextChange={setContext}
            initialContext={context}
          />
        </div>

        {/* Status Messages */}
        {statusMessage.message && (
          <div className={`rounded-2xl border p-4 flex items-center gap-3 ${statusMessage.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {statusMessage.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            <p className="text-sm font-medium">{statusMessage.message}</p>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-rose-700 text-sm">
            {error}
          </div>
        )}

        {/* Recalculation Progress */}
        {recalcProgress && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-bold text-slate-900">Recalculation Complete</p>
                  <p className="text-xs text-slate-500">
                    {recalcProgress.success} successful, {recalcProgress.failed} failed out of {recalcProgress.total}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setRecalcProgress(null)} 
                className="text-sm text-slate-400 hover:text-slate-600"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {!isContextReady ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-10 text-center">
            <Layers3 className="w-10 h-10 mx-auto text-indigo-500" />
            <h2 className="text-lg font-bold text-slate-900 mt-4">Select academic context</h2>
            <p className="text-sm text-slate-500 mt-1">Choose program type, program, academic year, and semester or term to load end exam marks.</p>
          </div>
        ) : loading ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-10 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-indigo-600 animate-spin" />
            <p className="text-sm text-slate-500 mt-3">Loading end exam data...</p>
          </div>
        ) : (
          <>
            {/* Subject Selection - Horizontal Scrollable Cards */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold text-slate-900">Subjects</h2>
                  <span className="text-sm text-slate-500">({subjects.length})</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative w-48">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search subjects..."
                      className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </div>
                  <button
                    onClick={handleRecalculateAllSubjects}
                    disabled={recalculatingAll || subjects.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50 transition-colors"
                  >
                    <Zap className={`w-3.5 h-3.5 ${recalculatingAll ? 'animate-spin' : ''}`} />
                    {recalculatingAll ? 'Processing...' : 'Recalc All'}
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300">
                {filteredSubjects.length === 0 ? (
                  <div className="text-sm text-slate-500 py-4 w-full text-center">No subjects found</div>
                ) : filteredSubjects.map((subject) => {
                  const isSelected = String(selectedSubject?.subjectId) === String(subject.subjectId);
                  return (
                    <button
                      key={subject.subjectId}
                      onClick={() => openSubject(subject)}
                      className={`flex-shrink-0 min-w-[200px] text-left rounded-xl border p-4 transition-all ${
                        isSelected 
                          ? 'border-indigo-300 bg-indigo-50/80 shadow-md ring-2 ring-indigo-200' 
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <BookOpen className={`w-4 h-4 ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                        <span className="font-bold text-sm text-slate-900 truncate">{subject.subjectName}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{subject.subjectCode}</span>
                        <span className="text-emerald-600 font-semibold">{formatPercent(subject.averageAggregatedPercentage)}</span>
                      </div>
                      <div className="mt-2 flex gap-2 text-[10px] text-slate-500">
                        <span>{subject.rawStudents} students</span>
                        <span>•</span>
                        <span>{subject.aggregatedStudents} aggregated</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Subject Details */}
            {selectedSubject && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Subject Header */}
                <div className="border-b border-slate-200 bg-gradient-to-r from-indigo-50/50 to-slate-50/50 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <BookOpen className="w-6 h-6 text-indigo-600" />
                        <h2 className="text-xl font-bold text-slate-900">{selectedSubjectStats?.subjectName}</h2>
                        <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-mono font-bold">
                          {selectedSubjectStats?.subjectCode}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span>Type: {selectedSubjectStats?.type || 'Theory'}</span>
                        {selectedSubjectStats?.isLab && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs">Lab</span>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {summaryStats && (
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-500">{summaryStats.totalStudents} Students</span>
                          <span className="text-emerald-600 font-semibold">{formatPercent(summaryStats.avgPercentage)}</span>
                          <span className="text-green-600">{summaryStats.passed} Passed</span>
                          <span className="text-rose-600">{summaryStats.failed} Failed</span>
                        </div>
                      )}
                      <button
                        onClick={handleRecalculateSubject}
                        disabled={recalculating || subjectStudents.length === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        <RotateCw className={`w-3.5 h-3.5 ${recalculating ? 'animate-spin' : ''}`} />
                        {recalculating ? 'Recalculating...' : 'Recalc Subject'}
                      </button>
                      <button
                        onClick={() => setViewMode(viewMode === 'summary' ? 'detailed' : 'summary')}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {viewMode === 'summary' ? <Eye className="w-3.5 h-3.5" /> : <Edit2 className="w-3.5 h-3.5" />}
                        {viewMode === 'summary' ? 'Summary' : 'Detailed'}
                      </button>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  {summaryStats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Students</p>
                        <p className="text-lg font-black text-slate-900">{summaryStats.totalStudents}</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">With Marks</p>
                        <p className="text-lg font-black text-slate-900">{summaryStats.withMarks}</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Aggregated</p>
                        <p className="text-lg font-black text-slate-900">{summaryStats.withAggregation}</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Avg %</p>
                        <p className="text-lg font-black text-indigo-600">{formatPercent(summaryStats.avgPercentage)}</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Passed</p>
                        <p className="text-lg font-black text-emerald-600">{summaryStats.passed}</p>
                      </div>
                      <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Failed</p>
                        <p className="text-lg font-black text-rose-600">{summaryStats.failed}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bulk Enhancement */}
                <div className="border-b border-slate-200 bg-slate-50/50 p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <WandSparkles className="w-5 h-5 text-indigo-600" />
                      <div>
                        <h3 className="font-bold text-slate-900 text-sm">Bulk Enhancement</h3>
                        <p className="text-xs text-slate-500">Increase raw marks by percentage and recalculate aggregation</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                        <input 
                          type="checkbox" 
                          checked={bulkOnlyBelowMax} 
                          onChange={(e) => setBulkOnlyBelowMax(e.target.checked)} 
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Only below max
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={bulkPercent}
                          onChange={(e) => setBulkPercent(e.target.value)}
                          className="w-20 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold"
                        />
                        <span className="text-sm text-slate-500">%</span>
                      </div>
                      <button
                        onClick={handleBulkEnhance}
                        disabled={bulkEnhancing || !selectedSubject}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        <WandSparkles className={`w-4 h-4 ${bulkEnhancing ? 'animate-pulse' : ''}`} />
                        {bulkEnhancing ? 'Processing...' : 'Enhance All'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Students Table */}
                <div className="p-4 sm:p-6">
                  {detailLoading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="w-8 h-8 mx-auto text-indigo-600 animate-spin" />
                      <p className="text-sm text-slate-500 mt-3">Loading student data...</p>
                    </div>
                  ) : subjectStudents.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-10 h-10 mx-auto text-slate-300" />
                      <p className="text-sm text-slate-500 mt-3">No students found for this subject</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {subjectStudents.map((student) => {
                        const agg = student.aggregation;
                        const rawEntries = student.rawEntries || [];
                        const isExpanded = expandedStudent === String(student.studentId);
                        const hasMarks = rawEntries.length > 0;
                        const totalRaw = student.rawTotalMarks || 0;
                        const totalMax = student.rawMaxMarks || 0;
                        const aggScore = agg?.totalEndExamScore || 0;
                        const aggMax = agg?.totalEndExamMaxMarks || 0;
                        const aggPercent = agg?.endExamPercentage || 0;
                        const status = agg?.status || 'pending';

                        return (
                          <div key={String(student.studentId)} className="border border-slate-200 rounded-2xl overflow-hidden">
                            <div 
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white hover:bg-slate-50/50 cursor-pointer transition-colors"
                              onClick={() => toggleStudentExpand(String(student.studentId))}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                                    {student.studentName?.charAt(0) || 'S'}
                                  </div>
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-bold text-slate-900 truncate">{student.studentName || 'Unknown'}</h4>
                                    <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                      {student.studentUSN}
                                    </span>
                                    <StatusBadge status={status} />
                                  </div>
                                  <p className="text-xs text-slate-500 truncate">{student.className || 'No class'}</p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-4 flex-wrap">
                                <div className="flex items-center gap-3 text-sm">
                                  <span className="text-slate-500">
                                    Raw: <span className="font-bold text-slate-900">{totalRaw}/{totalMax}</span>
                                  </span>
                                  <span className="text-slate-300">|</span>
                                  <span className="text-slate-500">
                                    Agg: <span className="font-bold text-indigo-600">{formatNumber(aggScore)}/{aggMax}</span>
                                  </span>
                                  <span className="text-slate-300">|</span>
                                  <span className={`font-bold ${aggPercent >= 40 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                    {formatPercent(aggPercent)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {isExpanded ? (
                                    <ChevronUp className="w-5 h-5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="border-t border-slate-200 bg-slate-50/50 p-4 space-y-4">
                                {hasMarks ? (
                                  <div className="space-y-3">
                                    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                                      <FileText className="w-3.5 h-3.5" /> Raw Marks Entries
                                    </h5>
                                    {rawEntries.map((entry) => (
                                      <div key={entry.markId || entry.assignmentId} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                          <div>
                                            <p className="font-bold text-slate-900 text-sm">{entry.endExamItemName || 'End Exam Item'}</p>
                                            <p className="text-xs text-slate-500">Max: {entry.totalMaxMarks || 0}</p>
                                          </div>
                                          <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                              <input
                                                type="number"
                                                min="0"
                                                max={entry.totalMaxMarks || 0}
                                                value={rawMarks[entry._id] ?? entry.totalMarksObtained ?? 0}
                                                onChange={(e) => setRawMarks((prev) => ({ ...prev, [entry._id]: e.target.value }))}
                                                className="w-24 px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold focus:ring-2 focus:ring-indigo-100 outline-none"
                                              />
                                              <span className="text-xs text-slate-500">/ {entry.totalMaxMarks || 0}</span>
                                            </div>
                                            <button
                                              onClick={() => handleSaveRawMark(entry)}
                                              disabled={saving}
                                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                                            >
                                              <Save className="w-3.5 h-3.5" /> Save
                                            </button>
                                          </div>
                                        </div>

                                        {Array.isArray(entry.questionWiseMarks) && entry.questionWiseMarks.length > 0 && (
                                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                                            {entry.questionWiseMarks.map((question) => {
                                              const questionKey = `${entry._id}:${question.questionId || question.questionNumber || question.sectionName || question._id || 'question'}`;
                                              return (
                                                <div key={questionKey} className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                                                  <span className="text-xs text-slate-600 truncate">
                                                    {question.sectionName || 'Q'}{question.questionNumber ? ` ${question.questionNumber}` : ''}
                                                  </span>
                                                  <div className="flex items-center gap-1">
                                                    <input
                                                      type="number"
                                                      min="0"
                                                      max={question.maxMarks || 0}
                                                      value={questionMarks[questionKey] ?? question.obtainedMarks ?? question.marksObtained ?? 0}
                                                      onChange={(e) => setQuestionMarks((prev) => ({ ...prev, [questionKey]: e.target.value }))}
                                                      className="w-16 px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs font-semibold focus:ring-2 focus:ring-indigo-100 outline-none"
                                                    />
                                                    <span className="text-[10px] text-slate-400">/{question.maxMarks || 0}</span>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-sm text-slate-500 bg-white rounded-xl border border-dashed border-slate-300">
                                    No raw marks entered for this student
                                  </div>
                                )}

                                {agg && (
                                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                      <div>
                                        <h5 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                                          <Award className="w-3.5 h-3.5" /> Aggregated End Exam Score
                                        </h5>
                                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-slate-500">Score:</span>
                                            <input
                                              type="number"
                                              min="0"
                                              max={agg.totalEndExamMaxMarks || 0}
                                              value={aggregationEdits[agg.id] ?? agg.totalEndExamScore ?? 0}
                                              onChange={(e) => setAggregationEdits((prev) => ({ ...prev, [agg.id]: e.target.value }))}
                                              className="w-20 px-2 py-1 rounded-lg border border-slate-200 bg-white text-sm font-bold focus:ring-2 focus:ring-indigo-100 outline-none"
                                            />
                                            <span className="text-sm text-slate-500">/ {agg.totalEndExamMaxMarks || 0}</span>
                                          </div>
                                          <span className="text-sm text-slate-300">|</span>
                                          <span className="text-sm text-slate-500">
                                            Contribution: <span className="font-bold text-slate-900">{formatNumber(agg.contributionToFinal || 0)}</span>
                                          </span>
                                          <span className="text-sm text-slate-300">|</span>
                                          <span className={`text-sm font-bold ${aggPercent >= 40 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                            {formatPercent(aggPercent)}
                                          </span>
                                          <StatusBadge status={agg.status} />
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleSaveAggregation(student)}
                                        disabled={saving}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                                      >
                                        <Save className="w-4 h-4" /> Update Aggregation
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* ============================================================ */}
      {/* MODALS */}
      {/* ============================================================ */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        isLoading={confirmationModal.isLoading}
      />

      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={closeResult}
        type={resultModal.type}
        title={resultModal.title}
        message={resultModal.message}
        details={resultModal.details}
      />
    </div>
  );
};

export default EndExamMarksManager;